import React, { useState, useEffect, useCallback } from "react";
import { Spinner, Row, Col, Alert } from "reactstrap";
import useCustomMutation from "../../Hooks/useCustomMutation";

function MapVenue({ userID, token, eventDetails, onUpdate }) {
  const [inputs, setInputs] = useState({ venueMap: '' });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const key = ["EventDetails", eventDetails?._id];
  const mutation = useCustomMutation(key);

  // ✅ Helper: Extract map URL from iframe or direct URL
  const extractMapUrl = useCallback((mapData) => {
    if (!mapData) return null;
    
    if (typeof mapData === "string" && mapData.trim().startsWith("<iframe")) {
      const match = mapData.match(/src\s*=\s*["']([^"']+)["']/i);
      return match?.[1] || null;
    }
    
    if (typeof mapData === "string" && (mapData.startsWith("http") || mapData.startsWith("//"))) {
      return mapData;
    }
    
    return null;
  }, []);

  // Initialize from eventDetails
  useEffect(() => {
    if (eventDetails?.venueMap) {
      setInputs({ venueMap: eventDetails.venueMap });
      setPreviewUrl(extractMapUrl(eventDetails.venueMap));
    }
  }, [eventDetails?.venueMap, extractMapUrl]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((values) => ({ ...values, [name]: value }));
    
    // Update preview as user types
    if (name === 'venueMap') {
      setPreviewUrl(extractMapUrl(value));
      setError(null);
    }
  };

  const handleSubmitForm = async (event) => {
    event.preventDefault();
    setError(null);

    // Validate: Check if it's a valid map embed
    const mapUrl = extractMapUrl(inputs.venueMap);
    if (inputs.venueMap && !mapUrl) {
      setError('Please enter a valid Google Maps embed code or URL');
      return;
    }

    const Method = "PATCH";
    const url = `events/${eventDetails?._id}`;
    const formdata = new FormData();
    
    if (inputs.venueMap) {
      formdata.append('venueMap', inputs.venueMap);
    }

    const data = {
      endPoint: url,
      bodyContent: formdata,
      Method: Method,
      displayMessage: "Event location updated successfully",
      token: token,
      isJSON: true,
    };

    try {
      await mutation.mutateAsync(data);
      
      // ✅ Notify parent builder to sync the change
      if (onUpdate && inputs.venueMap) {
        onUpdate('venueMap', inputs.venueMap);
      }
      
      // Update preview after successful save
      setPreviewUrl(extractMapUrl(inputs.venueMap));
      
    } catch (err) {
      setError('Failed to update location. Please try again.');
      console.error('MapVenue save error:', err);
    }
  };

  return (
    <div className="d-flex justify-content-center" style={{ width: '100%', maxWidth: 650 }}>
      <form className="form-create-event" onSubmit={handleSubmitForm}>
        {/* Error Alert */}
        {error && (
          <Alert color="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        {/* Success Message */}
        {mutation.isSuccess && (
          <Alert color="success" className="mb-3">
            Location updated successfully!
          </Alert>
        )}

        <Row>
          <Col sm={12}>
            <label className="event__form__Creation">
              Event Map Location 
              <small className="text-muted d-block" style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>
                Paste Google Maps embed code or URL
              </small>
            </label>
            
            <input
              type="text"
              name="venueMap"
              placeholder="e.g., https://www.google.com/maps/embed?pb=... or <iframe src=...>"
              value={inputs.venueMap}
              onChange={handleChange}
              className="form-control"
              style={{ minHeight: '80px', fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
            
            {/* Live Preview */}
            {previewUrl && (
              <div className="mt-3" style={{ 
                border: '1px solid #e2e8f0', 
                borderRadius: '8px', 
                overflow: 'hidden' 
              }}>
                <small className="text-muted px-3 py-2 d-block" style={{ fontSize: '0.75rem' }}>
                  Preview:
                </small>
                <iframe
                  src={previewUrl}
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Map preview"
                ></iframe>
              </div>
            )}
            
            {/* Help Text */}
            <small className="text-muted d-block mt-2" style={{ fontSize: '0.75rem' }}>
              💡 <strong>How to get embed code:</strong><br/>
              1. Go to Google Maps → Find your location<br/>
              2. Click "Share" → "Embed a map"<br/>
              3. Copy the HTML code or URL
            </small>
          </Col>
        </Row>

        <div className="d-flex justify-content-start align-items-center mt-3 gap-2">
          <button 
            className="btn btn-primary rounded-5" 
            type="submit"
            disabled={mutation.isLoading || !inputs.venueMap.trim()}
          >
            <div className="d-flex justify-content-center align-items-center gap-2">
              {mutation.isLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" /> 
                  <span>Updating...</span>
                </>
              ) : (
                <span>Update Map</span>
              )}
            </div>
          </button>
          
          {/* Clear Button */}
          {inputs.venueMap && (
            <button
              type="button"
              className="btn btn-outline-secondary rounded-5"
              onClick={() => {
                setInputs({ venueMap: '' });
                setPreviewUrl(null);
                setError(null);
                if (onUpdate) onUpdate('venueMap', '');
              }}
              disabled={mutation.isLoading}
            >
              Clear
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default MapVenue;